// â”€â”€â”€ Contact slide-over (Phase 3: view + edit + actions) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Toggles between view mode (read-only sections) and edit mode (form).
// Action buttons: Edit, Deactivate/Reactivate, Merge, Delete (guarded).

import React, { useState } from 'react';
import type { Contact } from '../types';
import {
  CONTACT_STATUS_LABELS,
  DELIVERY_PREFERENCE_LABELS,
  USER_TYPE_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  GRADE_LABELS,
} from '../types';
import { formatPhoneDisplay } from '../lib/phone';
import { isSyntheticEmail } from '../lib/email';
import ContactEditForm from './ContactEditForm';
import PromoteContactModal from './PromoteContactModal';
import {
  updateContact,
  deactivateContact,
  reactivateContact,
  deleteContact,
  demoteContactUser,
} from '../services/contactsService';
import type { ContactUpdates } from '../services/contactsService';

interface Props {
  contact: Contact | null;
  onClose: () => void;
  onChanged: () => void;
  onMergeRequested: (contact: Contact) => void;
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-3 py-2.5 border-b border-stone-50 last:border-0">
    <dt className="text-xs font-medium text-stone-500 uppercase tracking-wide">{label}</dt>
    <dd className="col-span-2 text-sm text-stone-800 break-words">{children}</dd>
  </div>
);

const Empty: React.FC = () => <span className="text-stone-300">â€”</span>;

function formatTimestamp(ts: any): string {
  if (!ts) return 'â€”';
  try {
    if (typeof ts.toDate === 'function') return ts.toDate().toLocaleString();
    if (ts instanceof Date) return ts.toLocaleString();
    return String(ts);
  } catch {
    return String(ts);
  }
}

const ContactSlideOver: React.FC<Props> = ({ contact, onClose, onChanged, onMergeRequested }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);

  const open = !!contact;

  // Reset edit state when contact changes
  React.useEffect(() => {
    setEditing(false);
    setError(null);
    setConfirmDelete(false);
  }, [contact?.id]);

  async function handleSave(updates: ContactUpdates | any) {
    if (!contact) return;
    setBusy('save');
    setError(null);
    try {
      await updateContact(contact.id, updates as ContactUpdates);
      setEditing(false);
      onChanged();
    } catch (e: any) {
      setError(e?.message ?? String(e));
      throw e; // let form display too
    } finally {
      setBusy(null);
    }
  }

  async function handleDeactivate() {
    if (!contact) return;
    setBusy('deactivate'); setError(null);
    try {
      await deactivateContact(contact.id);
      onChanged();
    } catch (e: any) { setError(e?.message ?? String(e)); }
    finally { setBusy(null); }
  }

  async function handleReactivate() {
    if (!contact) return;
    setBusy('reactivate'); setError(null);
    try {
      await reactivateContact(contact.id);
      onChanged();
    } catch (e: any) { setError(e?.message ?? String(e)); }
    finally { setBusy(null); }
  }

  async function handleDelete() {
    if (!contact) return;
    setBusy('delete'); setError(null);
    try {
      await deleteContact(contact.id);
      onChanged();
      onClose();
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setConfirmDelete(false);
    } finally { setBusy(null); }
  }

  async function handleDemote() {
    if (!contact) return;
    setBusy('demote'); setError(null);
    try {
      await demoteContactUser(contact.id, 'Demoted via admin UI');
      onChanged();
    } catch (e: any) { setError(e?.message ?? String(e)); }
    finally { setBusy(null); }
  }

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/30 transition-opacity z-40 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      <aside
        className={`fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-xl border-l border-stone-200 z-50 transform transition-transform overflow-y-auto ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {contact && (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-6 py-5 border-b border-stone-100">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  {contact.photoURL ? (
                    <img src={contact.photoURL} className="w-12 h-12 rounded-full flex-shrink-0" alt="" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#011132]/10 flex items-center justify-center text-[#011132] text-base font-bold flex-shrink-0">
                      {contact.displayName?.[0]?.toUpperCase() ?? '?'}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-bold text-stone-900 truncate">
                      {contact.displayName}
                    </h2>
                    <p className="text-sm text-stone-500 truncate">
                      {isSyntheticEmail(contact.email) ? (
                        <span className="italic text-stone-400">synthetic email â€” uses phone</span>
                      ) : (
                        contact.email
                      )}
                    </p>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      <span
                        className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${
                          contact.status === 'active'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-stone-100 text-stone-500 border-stone-200'
                        }`}
                      >
                        {CONTACT_STATUS_LABELS[contact.status]}
                      </span>
                      {contact.needsReview && (
                        <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 border-amber-200">
                          Needs Review
                        </span>
                      )}
                      {contact.hasAppAccount && (
                        <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 border-blue-200">
                          App User
                        </span>
                      )}
                      {contact.isExternal && (
                        <span className="text-[10px] font-semibold border rounded-full px-2 py-0.5 bg-orange-50 text-orange-700 border-orange-200">
                          External
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-stone-400 hover:text-stone-700 flex-shrink-0"
                  aria-label="Close"
                >
                  <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
                </button>
              </div>

              {/* Action buttons */}
              {!editing && (
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  <button
                    onClick={() => setEditing(true)}
                    disabled={busy !== null}
                    className="text-xs px-3 py-1.5 rounded border border-stone-200 text-stone-700 hover:bg-stone-50 font-medium"
                  >
                    Edit
                  </button>
                  {contact.status === 'active' ? (
                    <button
                      onClick={handleDeactivate}
                      disabled={busy !== null}
                      className="text-xs px-3 py-1.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 font-medium"
                    >
                      {busy === 'deactivate' ? 'â€¦' : 'Deactivate'}
                    </button>
                  ) : (
                    <button
                      onClick={handleReactivate}
                      disabled={busy !== null}
                      className="text-xs px-3 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium"
                    >
                      {busy === 'reactivate' ? 'â€¦' : 'Reactivate'}
                    </button>
                  )}
                  <button
                    onClick={() => onMergeRequested(contact)}
                    disabled={busy !== null || contact.status !== 'active'}
                    className="text-xs px-3 py-1.5 rounded border border-stone-200 text-stone-700 hover:bg-stone-50 font-medium disabled:opacity-50"
                  >
                    Mergeâ€¦
                  </button>
                  {contact.hasAppAccount ? (
                    <button
                      onClick={handleDemote}
                      disabled={busy !== null}
                      className="text-xs px-3 py-1.5 rounded border border-orange-300 text-orange-700 hover:bg-orange-50 font-medium"
                      title="Disable Firebase Auth + flip hasAppAccount to false"
                    >
                      {busy === 'demote' ? 'â€¦' : 'Demote'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setPromoteOpen(true)}
                      disabled={busy !== null || contact.status !== 'active'}
                      className="text-xs px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 font-medium disabled:opacity-50"
                    >
                      Grant Login
                    </button>
                  )}
                  {confirmDelete ? (
                    <>
                      <button
                        onClick={handleDelete}
                        disabled={busy !== null}
                        className="text-xs px-3 py-1.5 rounded border border-red-400 bg-red-50 text-red-700 hover:bg-red-100 font-medium"
                      >
                        {busy === 'delete' ? 'â€¦' : 'Confirm Delete'}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(false)}
                        disabled={busy !== null}
                        className="text-xs px-3 py-1.5 rounded text-stone-500 hover:bg-stone-50"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(true)}
                      disabled={busy !== null || contact.hasAppAccount}
                      className="text-xs px-3 py-1.5 rounded text-red-600 hover:bg-red-50 font-medium ml-auto disabled:opacity-50"
                      title={contact.hasAppAccount ? 'Demote app user first' : 'Hard delete'}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}

              {error && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                  {error}
                </div>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 px-6 py-4 space-y-5">
              {editing ? (
                <ContactEditForm
                  mode="edit"
                  initial={contact}
                  submitLabel="Save Changes"
                  onSubmit={handleSave}
                  onCancel={() => { setEditing(false); setError(null); }}
                />
              ) : (
                <>
                  {/* Merge pointer warning */}
                  {contact.mergedIntoContactId && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                      This contact was merged into{' '}
                      <code className="font-mono">{contact.mergedIntoContactId}</code>.
                    </div>
                  )}

                  {/* Identity */}
                  <section>
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Identity</h3>
                    <dl>
                      <Row label="Email">{contact.email}</Row>
                      <Row label="Phone">{contact.phone ? formatPhoneDisplay(contact.phone) : <Empty />}</Row>
                      <Row label="User Type">{contact.user_type ? USER_TYPE_LABELS[contact.user_type] : <Empty />}</Row>
                      <Row label="Employee ID">{contact.employee_id ?? <Empty />}</Row>
                    </dl>
                  </section>

                  {/* HRIS */}
                  <section>
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">HRIS</h3>
                    <dl>
                      <Row label="Job Title">{contact.jobTitle ?? <Empty />}</Row>
                      <Row label="Department">{contact.department ?? <Empty />}</Row>
                      <Row label="Grade">{contact.grade ? (GRADE_LABELS as any)[contact.grade] ?? contact.grade : <Empty />}</Row>
                      <Row label="Employment">{contact.employment_type ? EMPLOYMENT_TYPE_LABELS[contact.employment_type] : <Empty />}</Row>
                      <Row label="Site">{contact.site ?? <Empty />}</Row>
                      <Row label="Start Date">{contact.startDate ?? <Empty />}</Row>
                    </dl>
                  </section>

                  {/* Org */}
                  <section>
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Org Structure</h3>
                    <dl>
                      <Row label="Unit">{contact.unitName ?? contact.unitId ?? <Empty />}</Row>
                      <Row label="Manager ID">{contact.directManagerId ?? <Empty />}</Row>
                      <Row label="Is Leader">{contact.isLeader ? 'Yes' : 'No'}</Row>
                    </dl>
                  </section>

                  {/* Delivery */}
                  <section>
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Delivery</h3>
                    <dl>
                      <Row label="Preference">{contact.delivery_preference ? DELIVERY_PREFERENCE_LABELS[contact.delivery_preference] : <Empty />}</Row>
                      <Row label="Phone Verified">{contact.phoneVerified ? 'Yes' : 'No'}</Row>
                      <Row label="SMS Capable">{contact.phoneCanReceiveSms === false ? 'No' : contact.phoneCanReceiveSms === true ? 'Yes' : <Empty />}</Row>
                    </dl>
                  </section>

                  {/* App account */}
                  <section>
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">App Account</h3>
                    <dl>
                      <Row label="Has Login">{contact.hasAppAccount ? 'Yes' : 'No'}</Row>
                      <Row label="App User UID">{contact.appUserUid ? <code className="text-xs">{contact.appUserUid}</code> : <Empty />}</Row>
                    </dl>
                  </section>

                  {/* Provenance */}
                  <section>
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Provenance</h3>
                    <dl>
                      <Row label="Source">{contact.source}</Row>
                      <Row label="First Seen">{formatTimestamp(contact.firstSeenAt)}</Row>
                      <Row label="First Seen By"><code className="text-xs">{contact.firstSeenBy}</code></Row>
                      <Row label="Manual Edits">
                        {contact.manualEdits?.length > 0
                          ? <code className="text-xs">{contact.manualEdits.join(', ')}</code>
                          : <Empty />}
                      </Row>
                    </dl>
                  </section>

                  {/* M365 */}
                  {(contact.m365ObjectId || contact.lastSyncedAt) && (
                    <section>
                      <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Microsoft 365</h3>
                      <dl>
                        <Row label="Object ID"><code className="text-xs">{contact.m365ObjectId}</code></Row>
                        <Row label="Last Synced">{formatTimestamp(contact.lastSyncedAt)}</Row>
                      </dl>
                    </section>
                  )}

                  {/* Timestamps */}
                  <section>
                    <h3 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">Timestamps</h3>
                    <dl>
                      <Row label="Created">{formatTimestamp(contact.createdAt)}</Row>
                      <Row label="Updated">{formatTimestamp(contact.updatedAt)}</Row>
                    </dl>
                  </section>
                </>
              )}
            </div>
          </div>
        )}
      </aside>

      <PromoteContactModal
        open={promoteOpen}
        contact={contact}
        onClose={() => setPromoteOpen(false)}
        onPromoted={() => { onChanged(); }}
      />
    </>
  );
};

export default ContactSlideOver;
